import type { ReleaseRow, ReleasesResponse } from '@/api/orders';

/**
 * The Make-live bands, and they are DATES — not catalog states.
 *
 * The street date is what makes a release urgent, and it does that whatever
 * stage the release is stuck at. A collection whose embargo lifts tomorrow and
 * whose products do not exist yet is the most urgent row on the page, not an
 * inert one: setup, receiving and unhiding all have to happen before tomorrow.
 *
 * So the band answers WHEN, and the row's chips answer WHAT IT IS WAITING ON.
 * No release is excluded for being uncheckable; only ones with no street date
 * at all fall out of the date order, and those sort by expected arrival.
 *
 * Shared between the view and the nav count so the badge on the rail and the
 * rows in the queue can never disagree about what is still open.
 */
export type ReleaseBand = 'early' | 'overdue' | 'soon' | 'upcoming' | 'undated' | 'live';

/** Days inside which a street date is imminent rather than upcoming. */
export const SOON_DAYS = 7;

/** Days from today to a date string, negative once past. */
export function daysUntil(date: string): number {
  return Math.ceil((new Date(`${date}T00:00:00`).getTime() - Date.now()) / 86_400_000);
}

export function releaseBand(r: ReleaseRow): ReleaseBand {
  // Fully visible is done — unless the embargo has NOT lifted yet, which means
  // we are selling early. The inverse mistake, and the only one this view can
  // catch after the fact.
  if (r.state === 'live') {
    return r.embargoDate && daysUntil(r.embargoDate) > 0 ? 'early' : 'live';
  }
  // Everything else is unfinished in some way — products hidden, products not
  // created, or no catalog link at all. What separates them is only how soon
  // it matters, which is the date and nothing else.
  if (!r.embargoDate) return 'undated';
  const d = daysUntil(r.embargoDate);
  if (d <= 0) return 'overdue';
  return d <= SOON_DAYS ? 'soon' : 'upcoming';
}

/**
 * The queue's size: every release with work left, i.e. anything not sitting
 * in the Live band. Undefined until the releases have been read — a badge
 * must never show 0 for "not loaded".
 */
export function releasesPending(data: ReleasesResponse | null): number | undefined {
  if (!data) return undefined;
  return data.releases.filter((r) => releaseBand(r) !== 'live').length;
}

/**
 * What a release is waiting on, for every state that yields no hidden count.
 * Two or three words for a chip, the full explanation for its tooltip. Shared
 * by the Make-live queue and the overview calendar so a release reads the
 * same on both.
 *
 * `unstamped` splits on needsSetup because the two cases wait on different
 * people. A release whose products do not exist yet is waiting on setup, and
 * the category stamp follows from it; one whose products DO exist is waiting
 * only on a run to stamp the category, and is otherwise ready to check.
 */
export function releaseWaitingOn(r: ReleaseRow): { label: string; hint: string } | null {
  switch (r.state) {
    case 'empty':
      return { label: 'not set up', hint: 'The category exists but holds no products yet.' };
    case 'unstamped':
      return r.needsSetup
        ? { label: 'not set up', hint: 'No catalog category and no products yet.' }
        : { label: 'not linked', hint: 'No category stamp yet; the next run stamps it.' };
    case 'unknown':
      return { label: 'check failed', hint: 'The live catalog could not be read.' };
    case 'unresolvable':
      return { label: 'bad category', hint: 'The stamped category does not resolve in the catalog.' };
    default:
      return null;
  }
}
